import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Shield, Database, Clock, Mail, User, FileText } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const PrivacyPage = () => {
  const [companyProfile, setCompanyProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCompanyProfile();
  }, []);

  const fetchCompanyProfile = async () => {
    try {
      // Public endpoint to get basic company info for privacy policy
      const response = await axios.get(`${API}/company-profile/public`);
      setCompanyProfile(response.data);
    } catch (error) {
      // Use default if not available
      setCompanyProfile({
        company_name: "Gavs Bemanning AB",
        email: "info@gavsbemanning.se",
        address: "Sweden"
      });
    } finally {
      setLoading(false);
    }
  };

  const currentYear = new Date().getFullYear();

  return (
    <div className="min-h-screen bg-gray-50" data-testid="privacy-page">
      {/* Header */}
      <div className="bg-[#F37021] text-white py-6">
        <div className="max-w-4xl mx-auto px-6">
          <Link to="/login" className="inline-flex items-center text-white/80 hover:text-white mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Login
          </Link>
          <div className="flex items-center gap-3">
            <Shield className="w-10 h-10" />
            <div>
              <h1 className="text-3xl font-bold">Privacy Policy</h1>
              <p className="text-white/80">A-Track Timesheet Management System</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-10">
        <div className="bg-white rounded-xl shadow-sm p-8 space-y-8">
          
          {/* Last Updated */}
          <p className="text-sm text-gray-500">
            Last updated: {currentYear}-01-01
          </p>

          {/* Introduction */}
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
              <Shield className="w-5 h-5 text-[#F37021]" />
              Introduction
            </h2>
            <p className="text-gray-700 leading-relaxed">
              This Privacy Policy explains how {companyProfile?.company_name || "Gavs Bemanning AB"} (referred to as &quot;we&quot;, &quot;us&quot;, &quot;our&quot;) 
              collects, uses, and protects personal data through the A-Track Timesheet Management System. 
              We are committed to protecting your privacy and handling your data in accordance with the 
              General Data Protection Regulation (GDPR) and Swedish data protection laws.
            </p>
          </section>

          {/* Data Controller */}
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
              <User className="w-5 h-5 text-[#F37021]" />
              Data Controller
            </h2>
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="font-semibold text-gray-900">{companyProfile?.company_name || "Gavs Bemanning AB"}</p>
              {companyProfile?.address && <p className="text-gray-600">{companyProfile.address}</p>}
              {companyProfile?.email && (
                <p className="text-gray-600">
                  Email: <a href={`mailto:${companyProfile.email}`} className="text-[#F37021] hover:underline">{companyProfile.email}</a>
                </p>
              )}
              {companyProfile?.phone && <p className="text-gray-600">Phone: {companyProfile.phone}</p>}
              {companyProfile?.registration_number && (
                <p className="text-gray-600">Org. nr: {companyProfile.registration_number}</p>
              )}
            </div>
          </section>

          {/* What Data We Collect */}
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
              <Database className="w-5 h-5 text-[#F37021]" />
              What Personal Data We Collect
            </h2>
            <p className="text-gray-700 mb-4">We collect and process the following personal data:</p>
            <ul className="space-y-2 text-gray-700">
              <li className="flex items-start gap-2">
                <span className="text-[#F37021] font-bold">•</span>
                <span><strong>Identity information:</strong> Full name, personal number (personnummer)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#F37021] font-bold">•</span>
                <span><strong>Contact information:</strong> Email address, phone number, address</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#F37021] font-bold">•</span>
                <span><strong>Work records:</strong> Hours worked, work locations, project assignments (A-numbers)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#F37021] font-bold">•</span>
                <span><strong>Timesheet details:</strong> Start/end times, work descriptions, coworker names, notes</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#F37021] font-bold">•</span>
                <span><strong>Leave records:</strong> Sick leave, vacation, parental leave (VAB, pappaledigt)</span>
              </li>
            </ul>
          </section>

          {/* Why We Collect Data */}
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#F37021]" />
              Why We Collect This Data
            </h2>
            <p className="text-gray-700 mb-4">We process your personal data for the following purposes:</p>
            <ul className="space-y-2 text-gray-700">
              <li className="flex items-start gap-2">
                <span className="text-[#F37021] font-bold">•</span>
                <span><strong>Employment management:</strong> To manage your work assignments and scheduling</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#F37021] font-bold">•</span>
                <span><strong>Timesheet tracking:</strong> To accurately record hours worked for payroll and client invoicing</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#F37021] font-bold">•</span>
                <span><strong>Client reporting:</strong> To provide work reports to our client (Aggreko Sweden AB)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#F37021] font-bold">•</span>
                <span><strong>Legal compliance:</strong> To fulfill our obligations under Swedish employment and tax law</span>
              </li>
            </ul>
            <p className="text-gray-700 mt-4">
              <strong>Legal basis:</strong> We process your data based on the performance of our employment contract with you, 
              our legitimate business interests, and legal obligations.
            </p>
          </section>

          {/* Data Retention */}
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
              <Clock className="w-5 h-5 text-[#F37021]" />
              How Long We Keep Your Data
            </h2>
            <ul className="space-y-2 text-gray-700">
              <li className="flex items-start gap-2">
                <span className="text-[#F37021] font-bold">•</span>
                <span><strong>Active employment:</strong> Data is retained while you are an active team member</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#F37021] font-bold">•</span>
                <span><strong>After employment ends:</strong> Timesheet records are kept for 7 years as required by Swedish accounting law (Bokföringslagen)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#F37021] font-bold">•</span>
                <span><strong>Account data:</strong> Login credentials are deleted within 30 days of account deactivation</span>
              </li>
            </ul>
          </section>

          {/* Data Sharing */}
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">Who We Share Data With</h2>
            <p className="text-gray-700 mb-4">Your data may be shared with:</p>
            <ul className="space-y-2 text-gray-700">
              <li className="flex items-start gap-2">
                <span className="text-[#F37021] font-bold">•</span>
                <span><strong>Aggreko Sweden AB:</strong> Our client, for work assignment verification and invoicing</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#F37021] font-bold">•</span>
                <span><strong>Tax authorities:</strong> As required by Swedish law (Skatteverket)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#F37021] font-bold">•</span>
                <span><strong>Service providers:</strong> Secure cloud hosting and email services (data processed within EU/EEA)</span>
              </li>
            </ul>
            <p className="text-gray-700 mt-4">
              We do not sell your personal data to third parties.
            </p>
          </section>

          {/* Your Rights */}
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
              <Mail className="w-5 h-5 text-[#F37021]" />
              Your Rights
            </h2>
            <p className="text-gray-700 mb-4">Under GDPR, you have the right to:</p>
            <ul className="space-y-2 text-gray-700">
              <li className="flex items-start gap-2">
                <span className="text-[#F37021] font-bold">•</span>
                <span><strong>Access:</strong> Request a copy of the personal data we hold about you</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#F37021] font-bold">•</span>
                <span><strong>Rectification:</strong> Request correction of inaccurate or incomplete data</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#F37021] font-bold">•</span>
                <span><strong>Erasure:</strong> Request deletion of your data (subject to legal retention requirements)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#F37021] font-bold">•</span>
                <span><strong>Restriction:</strong> Request limitation of processing in certain circumstances</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#F37021] font-bold">•</span>
                <span><strong>Portability:</strong> Receive your data in a structured, commonly used format</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#F37021] font-bold">•</span>
                <span><strong>Objection:</strong> Object to processing based on legitimate interests</span>
              </li>
            </ul>
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mt-4">
              <p className="text-gray-700">
                To exercise any of these rights, please contact us at{" "}
                <a href={`mailto:${companyProfile?.email || "info@gavsbemanning.se"}`} className="text-[#F37021] font-semibold hover:underline">
                  {companyProfile?.email || "info@gavsbemanning.se"}
                </a>
              </p>
            </div>
          </section>

          {/* Complaints */}
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">Complaints</h2>
            <p className="text-gray-700">
              If you are not satisfied with how we handle your personal data, you have the right to lodge a complaint 
              with the Swedish Authority for Privacy Protection (Integritetsskyddsmyndigheten, IMY) at{" "}
              <a href="https://www.imy.se" target="_blank" rel="noopener noreferrer" className="text-[#F37021] hover:underline">
                www.imy.se
              </a>
            </p>
          </section>

          {/* Changes */}
          <section>
            <h2 className="text-xl font-bold text-gray-900 mb-3">Changes to This Policy</h2>
            <p className="text-gray-700">
              We may update this Privacy Policy from time to time. Any changes will be posted on this page 
              with an updated revision date. We encourage you to review this policy periodically.
            </p>
          </section>

        </div>

        {/* Footer */}
        <div className="text-center mt-8 text-gray-500 text-sm">
          <p>© {currentYear} {companyProfile?.company_name || "Gavs Bemanning AB"}. All rights reserved.</p>
          <p className="mt-2">
            <Link to="/login" className="text-[#F37021] hover:underline">Return to Login</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPage;
